import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  isAxiosError,
} from 'axios';
import { isNetworkError } from 'axios-retry';

import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import {
  GenericHttpError,
  type GenericHttpRequest,
  type GenericHttpResponse,
  type HttpHeaders,
  type HttpMethod,
} from '@iconik-io/core/src/http/generic_http_transport';
import type { Result } from '@iconik-io/core/src/result';
import retryWithExponentialBackOff, {
  AbortRetryError,
  RetryError,
} from '@iconik-io/core/src/retry_with_backoff';
import type { UploaderHttpClientInterface } from '@iconik-io/uploader/src/client/client';
import {
  GenericRetryError,
  type GenericRetryOptions,
  type UploaderHttpRequestOptions,
} from '@iconik-io/uploader/src/client/request';
import { WholeFileBody } from '@iconik-io/uploader/src/client/whole_file_body';
import { WholeBodyUnsupportedError } from '@iconik-io/uploader/src/errors';
import type { FileInfo } from '@iconik-io/uploader/src/state';
import { calculateRetryDelay } from '@iconik-io/uploader/src/utils/calculate_retry_delay';
import { shouldRetryErrorResponse } from '@iconik-io/uploader/src/utils/should_retry_error_response';

import { getRegisteredFile } from './file_registry';

const logger = DebugLogger.getInstance('uploader_http_client');

export const DEFAULT_RETRY_OPTIONS = {
  retries: 5,
  minTimeout: 0,
  maxTimeout: 60_000,
  method: ['get', 'put', 'head', 'delete'] satisfies HttpMethod[],
  status: [408, 429, 500, 502, 503, 504],
};

export class HttpAbortError extends Error {}

const fromAxiosResponse = <ResponseBody>(
  request: GenericHttpRequest,
  response: AxiosResponse<ResponseBody>,
) => ({
  request,
  body: response.data,
  status: response.status,
  statusText: response.statusText,
  // biome-ignore lint/plugin: axios AxiosHeaders is structurally the HttpHeaders record at this boundary.
  headers: response.headers as HttpHeaders,
});

const getWholeFile = ({ streamSource }: FileInfo): File => {
  if (streamSource.type !== 'fileRef') {
    throw new WholeBodyUnsupportedError(streamSource.type);
  }
  const file = getRegisteredFile(streamSource.id);
  if (!file) {
    throw new Error(`No file is registered for ${streamSource.id}`);
  }
  return file;
};

const toAxiosData = (body: unknown): unknown =>
  body instanceof WholeFileBody ? getWholeFile(body.fileInfo) : body;

const shouldRetry = (
  result: Result<GenericHttpResponse>,
  request: GenericHttpRequest,
  retryOptions: GenericRetryOptions | undefined,
): boolean => {
  // If it was a successful call we allow the client to check if we should retry
  // and default to not retrying
  if (result.ok) {
    if (retryOptions?.shouldRetry?.(result.value)) {
      logger.debug('Retrying based on retryOptions.shouldRetry');
      return true;
    }
    return false;
  }

  const error = result.error;
  if (error instanceof HttpAbortError) {
    return false;
  }

  const retry = { ...DEFAULT_RETRY_OPTIONS, ...retryOptions };
  logger.debug(
    'Checking if we should retry',
    request.method.toUpperCase(),
    request.url,
    error,
  );

  if (error instanceof GenericHttpError) {
    if (error.response) {
      if (retryOptions?.shouldRetry?.(error.response)) {
        logger.debug('Retrying based on retryOptions.shouldRetry');
        return true;
      }
    }
    if (shouldRetryErrorResponse(error, retry)) {
      logger.debug('Retrying status code', error.response.status);
      return true;
    }
  }

  if (isNetworkError(error)) {
    logger.debug('Retrying network error');
    return true;
  }

  logger.debug('Request not retried');
  return false;
};
export class HttpClient implements UploaderHttpClientInterface {
  client: AxiosInstance;

  constructor() {
    this.client = axios.create();
    this.client.defaults.headers.post['Content-Type'] = null;
  }

  /**
   * Return a URL string with the query parameters from the request.query
   * applied, replacing any existing parameters in the URL.
   *
   * Done explicitly instead of using axios `params` option for compatibility
   * with `got` library used by the agent.
   *
   * https://github.com/sindresorhus/got/blob/75287d683bbc4a1389aadef567864f1115625c7d/source/core/options.ts#L1573-L1628
   **/
  #getUrlWithSearchParams(request: GenericHttpRequest) {
    const url = new URL(request.url);

    if (request.query) {
      for (const [key, raw] of Object.entries(request.query)) {
        if (raw === undefined) {
          url.searchParams.delete(key);
          continue;
        }
        const value = raw === null ? '' : String(raw);
        url.searchParams.set(key, value);
      }
    }

    return url.toString();
  }

  #request(
    request: GenericHttpRequest,
    options: UploaderHttpRequestOptions,
  ): Promise<GenericHttpResponse> {
    logger.debug(`Making request: ${request.method} ${request.url}`);
    const { onUploadProgress, abortSignal } = options;

    // Construct URL with query parameters, replacing params in URL with those
    // provided in the request.query.
    const url = this.#getUrlWithSearchParams(request);

    // Use Axios fetch adapter when we need to send Content-Type without a body,
    // because the default XHR adapter strips Content-Type for empty bodies.
    // Required for S3 uploads.
    const needsFetchAdapter =
      request.body === undefined &&
      request.headers?.['Content-Type'] !== undefined;

    const requestConfig: AxiosRequestConfig = {
      method: request.method,
      url,
      headers: request.headers,
      data: toAxiosData(request.body),
      signal: abortSignal,
      adapter: needsFetchAdapter ? 'fetch' : undefined,
    };

    if (onUploadProgress) {
      requestConfig.onUploadProgress = (progressEvent) => {
        onUploadProgress({
          lengthComputable: progressEvent.lengthComputable,
          loaded: progressEvent.loaded,
          // Axios does not provide total in all cases, so we default to 0
          total: progressEvent.total ?? 0,
        });
      };
    }

    return this.client(requestConfig)
      .then((response) => fromAxiosResponse(request, response))
      .catch((error) => {
        if (!isAxiosError(error) || !error.response) {
          throw error;
        }
        throw new GenericHttpError(
          request,
          fromAxiosResponse(request, error.response),
        );
      });
  }

  request(
    request: GenericHttpRequest,
    options: UploaderHttpRequestOptions,
  ): Promise<GenericHttpResponse> {
    const { retryOptions } = options;
    const maxRetries = retryOptions?.retries ?? DEFAULT_RETRY_OPTIONS.retries;

    return retryWithExponentialBackOff(() => this.#request(request, options), {
      shouldRetry: ({ result }) =>
        shouldRetry(result, request, options.retryOptions),
      calculateDelay: ({ defaultDelay, result }) =>
        calculateRetryDelay({
          result,
          retryOptions: options.retryOptions,
          defaultDelay,
          logger,
        }),
      abortSignal: options.abortSignal,
      maxRetries,
    }).catch((error) => {
      // Translate the errors from retryWithExponentialBackoff to the types that
      // UploaderHttpClientInterface should throw
      if (error instanceof AbortRetryError) {
        throw new HttpAbortError();
      }
      if (error instanceof RetryError) {
        throw new GenericRetryError();
      }
      throw error;
    });
  }
}
